import type { CatalogWorkspace } from "types/user-catalog";

type LandingWorkspace = Pick<CatalogWorkspace, "workspace_id" | "role" | "created_at">;

const isOwner = (workspace: LandingWorkspace) => workspace.role?.toLowerCase() === "owner";

const timeOf = (value: string | undefined) => {
  const time = value ? new Date(value).getTime() : NaN;
  return Number.isNaN(time) ? 0 : time;
};

/**
 * The workspace to open when the app starts at "/", in this order:
 * 1. The last workspace the user opened, when they still belong to it.
 * 2. The workspace they joined most recently. For member rows the catalog's
 *    created_at is the membership date, so the newest one is the latest invite.
 * 3. Their default workspace: the first one they own, else the first listed.
 */
export function pickLandingWorkspace<T extends LandingWorkspace>(
  workspaces: T[],
  lastOpenedWorkspaceId: string | null | undefined
): T | null {
  if (workspaces.length === 0) return null;

  if (lastOpenedWorkspaceId) {
    const lastOpened = workspaces.find(
      (workspace) => String(workspace.workspace_id) === String(lastOpenedWorkspaceId)
    );
    if (lastOpened) return lastOpened;
  }

  const newestInvited = workspaces
    .filter((workspace) => !isOwner(workspace))
    .reduce<T | null>(
      (newest, workspace) =>
        !newest || timeOf(workspace.created_at) > timeOf(newest.created_at) ? workspace : newest,
      null
    );
  if (newestInvited) return newestInvited;

  return workspaces.find(isOwner) ?? workspaces[0];
}
